import React from "react";
import { trpcClient } from "@/lib/trpc";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertCircle, FileText, Activity, DollarSign, Warehouse, BarChart3, PieChart as PieIcon } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { KpiStatCard } from "@/components/shared/KpiStatCard";
import { RDonutChart } from "@/pages/dashboard/components/Charts";

const WAREHOUSE_COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#64748b",
];

export function WarehouseDashboard() {
  const { data: metricsData, isLoading: isLoadingMetrics } = useQuery({
    queryKey: ["inventory", "getDashboardMetrics"],
    queryFn: () => trpcClient.inventory.getDashboardMetrics.query(),
  });

  const { data: inventoryReport, isLoading: isLoadingReport } = useQuery({
    queryKey: ["reports", "inventory-dashboard-overview"],
    queryFn: () => trpcClient.reports.getInventoryData.query({ period: "thisMonth" }),
  });

  const isLoading = isLoadingMetrics || isLoadingReport;

  const totalTx7Days =
    metricsData?.chartData?.reduce(
      (acc: number, curr: any) => acc + (curr.receipt || 0) + (curr.issue || 0),
      0
    ) || 0;

  const invSummary = inventoryReport?.summary;
  const stockByWarehouse = inventoryReport?.stockByWarehouse || [];

  const donutData = stockByWarehouse.map((wh: any, idx: number) => ({
    label: wh.name || wh.code || `Kho ${idx + 1}`,
    value: wh.totalValue || 0,
    color: WAREHOUSE_COLORS[idx % WAREHOUSE_COLORS.length],
  }));

  return (
    <div className="space-y-4">
      {/* ═══ 4 THẺ KPI KHO VẬN CHUẨN PoP & KpiStatCard ═══ */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <KpiStatCard
          title="Định giá vốn kho"
          value={invSummary?.totalInventoryValue || 0}
          prevValue={invSummary?.prevInventoryValue}
          deltaPct={invSummary?.valueGrowth?.pct}
          diff={invSummary?.valueGrowth?.diff}
          formatType="currencyShort"
          colorVariant="emerald"
          icon={DollarSign}
          direction="positive"
          isLoading={isLoading}
          testId="wh-kpi-valuation"
        />

        <KpiStatCard
          title="Dưới tồn an toàn"
          value={metricsData?.lowStockCount || 0}
          unit="mã"
          formatType="number"
          colorVariant="rose"
          icon={AlertCircle}
          badgeText={(metricsData?.lowStockCount || 0) > 0 ? "Cần đặt thêm" : "An toàn"}
          direction="inverse"
          isLoading={isLoading}
          testId="wh-kpi-low-stock"
        />

        <KpiStatCard
          title="Chứng từ chờ duyệt"
          value={metricsData?.pendingDocs || 0}
          unit="phiếu"
          formatType="number"
          colorVariant="amber"
          icon={FileText}
          badgeText={(metricsData?.pendingDocs || 0) > 0 ? "Chờ duyệt" : "Đã duyệt"}
          direction="inverse"
          isLoading={isLoading}
          testId="wh-kpi-pending-docs"
        />

        <KpiStatCard
          title="Tổng giao dịch 7 ngày"
          value={totalTx7Days}
          unit="lượt"
          formatType="number"
          colorVariant="blue"
          icon={Activity}
          badgeText="7 ngày qua"
          direction="positive"
          isLoading={isLoading}
          testId="wh-kpi-tx"
        />
      </div>

      {/* ═══ 2 KHỐI BIỂU ĐỒ TRỰC QUAN (Hybrid Analytics) ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Biểu đồ Nhập - Xuất 7 Ngày Qua */}
        <Card className="shadow-2xs border border-border lg:col-span-2 bg-card">
          <CardHeader className="py-2.5 px-3.5 border-b border-border">
            <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4 text-primary" />
              Biến Động Nhập - Xuất (7 Ngày Qua)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3.5">
            <div className="h-[260px] w-full">
              <ResponsiveContainer
                width="100%"
                height={260}
                minWidth={1}
                minHeight={1}
                initialDimension={{ width: 100, height: 100 }}
              >
                <BarChart
                  data={metricsData?.chartData || []}
                  margin={{ top: 10, right: 15, left: -10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="name" fontSize={11} stroke="#64748b" tickLine={false} axisLine={false} />
                  <YAxis fontSize={11} stroke="#64748b" tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "11px" }}
                  />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
                  <Bar dataKey="receipt" name="Nhập kho" fill="#10b981" radius={[4, 4, 0, 0]} barSize={16} />
                  <Bar dataKey="issue" name="Xuất kho" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Biểu đồ Cơ cấu giá trị kho (Donut Chart) */}
        <Card className="shadow-2xs border border-border lg:col-span-1 bg-card flex flex-col">
          <CardHeader className="py-2.5 px-3.5 border-b border-border">
            <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground flex items-center gap-1.5">
              <PieIcon className="w-4 h-4 text-emerald-500" />
              Cơ Cấu Giá Trị Theo Kho
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3.5 flex-1 flex flex-col justify-center">
            {donutData.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-10 font-medium">
                Chưa có dữ liệu tồn kho
              </p>
            ) : (
              <RDonutChart data={donutData} totalLabel="Tổng giá trị" height={240} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
